/**
 * Interval Drill — free-practice mode.
 *
 * Generates interval drill rounds from an IntervalExerciseConfig.
 * Pure — no I/O, no audio APIs, no DOM.
 */

import { AudioPlanBuilder, type AudioPlan } from "./audio-plan.js";
import { Rng } from "./exercise.js";
import {
  appendCadence,
  pickKey,
  MODE_PATTERNS,
  MODE_DISPLAY_NAMES,
  type CadenceOptions,
  type KeyChoice,
} from "./utils.js";
import { buildIntervalTeachPlan } from "./generators/interval.js";

// ─── Types ────────────────────────────────────────────────────────────────────

export type IntervalSymbol =
  | "m2" | "M2" | "m3" | "M3" | "P4" | "TT"
  | "P5" | "m6" | "M6" | "m7" | "M7" | "P8";

export type IntervalDirection = "ascending" | "descending" | "harmonic" | "mixed";
export type IntervalKeyContext = "in-key" | "abstract";
export type IntervalBottomNote = "tonic" | "any-degree";

export interface IntervalExerciseConfig {
  /** Intervals to draw from, ≥1. */
  intervals: IntervalSymbol[];
  direction: IntervalDirection;
  keyContext: IntervalKeyContext;
  /** "fixed" = always fixedTonicMidi; "random" = random from common keys. Only relevant when keyContext === "in-key". */
  tonic: "fixed" | "random";
  /** MIDI note for the fixed tonic. Only used when tonic === "fixed" and keyContext === "in-key". */
  fixedTonicMidi: number;
  /** Where the bottom note of the interval is drawn from. Only relevant when keyContext === "in-key". */
  bottomNote: IntervalBottomNote;
  drillMode: "graded" | "listening" | "solfege-first";
  fsrsEnabled: boolean;
}

export interface IntervalExerciseRound {
  symbol: IntervalSymbol;
  semitones: number;
  /** Resolved direction for this round (never "mixed"). */
  effectiveDirection: Exclude<IntervalDirection, "mixed">;
  /** Lower-pitch MIDI note. */
  lowerMidi: number;
  /** Higher-pitch MIDI note. */
  upperMidi: number;
  /** Tonic MIDI (0 in abstract mode). */
  tonicMidi: number;
  /** Key display name (empty string in abstract mode). */
  keyName: string;
  /**
   * 0-based Ionian degree index of the bottom note (0 = tonic = degree 1).
   * null only in abstract mode (no key context).
   */
  degreeIndex: number | null;
  /**
   * 0-based Ionian degree index of the upper note.
   * null when the upper note is chromatic (not a diatonic scale degree),
   * or when keyContext === "abstract".
   */
  upperDegreeIndex: number | null;
  audioPlan: AudioPlan;
  teachAudio: AudioPlan;
}

// ─── Interval pool ────────────────────────────────────────────────────────────

export interface IntervalInfo {
  semitones: number;
  longName: string;
  group: string;
}

export const INTERVAL_POOL: Record<IntervalSymbol, IntervalInfo> = {
  "m2": { semitones: 1,  longName: "Minor 2nd",   group: "Seconds" },
  "M2": { semitones: 2,  longName: "Major 2nd",   group: "Seconds" },
  "m3": { semitones: 3,  longName: "Minor 3rd",   group: "Thirds" },
  "M3": { semitones: 4,  longName: "Major 3rd",   group: "Thirds" },
  "P4": { semitones: 5,  longName: "Perfect 4th", group: "4ths & 5ths" },
  "TT": { semitones: 6,  longName: "Tritone",     group: "4ths & 5ths" },
  "P5": { semitones: 7,  longName: "Perfect 5th", group: "4ths & 5ths" },
  "m6": { semitones: 8,  longName: "Minor 6th",   group: "Sixths" },
  "M6": { semitones: 9,  longName: "Major 6th",   group: "Sixths" },
  "m7": { semitones: 10, longName: "Minor 7th",   group: "Sevenths" },
  "M7": { semitones: 11, longName: "Major 7th",   group: "Sevenths" },
  "P8": { semitones: 12, longName: "Octave",      group: "Octave" },
};

/** Ordered list of all 12 interval symbols, semitone-ascending. */
export const INTERVAL_SYMBOLS_ORDERED: readonly IntervalSymbol[] = [
  "m2", "M2", "m3", "M3", "P4", "TT", "P5", "m6", "M6", "m7", "M7", "P8",
];

/** Quick-select presets. */
export const INTERVAL_PRESETS: Record<"consonants" | "diatonic" | "all", IntervalSymbol[]> = {
  consonants: ["m3", "M3", "P4", "P5", "m6", "M6", "P8"],
  diatonic:   ["m2", "M2", "m3", "M3", "P4", "P5", "m6", "M6", "m7", "M7", "P8"],
  all:        ["m2", "M2", "m3", "M3", "P4", "TT", "P5", "m6", "M6", "m7", "M7", "P8"],
};

/** Movable-do solfège for Ionian degrees 1–7 (index 0 = degree 1). */
export const IONIAN_SOLFEGE = ["do", "re", "mi", "fa", "sol", "la", "ti"] as const;

/**
 * For each interval, the 0-based Ionian degree indices of lower notes whose
 * upper note (lowerOffset + semitones mod 12) is also a diatonic degree.
 * Used by solfège-first mode to guarantee both notes are nameable in solfège.
 *
 * Derivation: for each (lowerDeg, interval) pair, check whether
 * (IONIAN_PATTERN[lowerDeg] + semitones) % 12 is in IONIAN_PATTERN.
 */
export const DIATONIC_LOWER_DEGREES: Record<IntervalSymbol, number[]> = {
  "m2": [2, 6],            // mi→fa, ti→do
  "M2": [0, 1, 3, 4, 5],  // do→re, re→mi, fa→sol, sol→la, la→ti
  "m3": [1, 2, 5, 6],     // re→fa, mi→sol, la→do, ti→re
  "M3": [0, 3, 4],        // do→mi, fa→la, sol→ti
  "P4": [0, 1, 2, 4, 5, 6], // do→fa, re→sol, mi→la, sol→do, la→re, ti→mi
  "TT": [3, 6],            // fa→ti, ti→fa
  "P5": [0, 1, 2, 3, 4, 5], // do→sol, re→la, mi→ti, fa→do, sol→re, la→mi
  "m6": [2, 5, 6],         // mi→do, la→fa, ti→sol
  "M6": [0, 1, 3, 4],     // do→la, re→ti, fa→re, sol→mi
  "m7": [1, 2, 4, 5, 6],  // re→do, mi→re, sol→fa, la→sol, ti→la
  "M7": [0, 3],            // do→ti, fa→mi
  "P8": [0, 1, 2, 3, 4, 5, 6],
};

// ─── Default config ───────────────────────────────────────────────────────────

export const DEFAULT_INTERVAL_EXERCISE_CONFIG: IntervalExerciseConfig = {
  intervals: [...INTERVAL_PRESETS.diatonic],
  direction: "ascending",
  keyContext: "in-key",
  tonic: "fixed",
  fixedTonicMidi: 60,
  bottomNote: "tonic",
  drillMode: "graded",
  fsrsEnabled: false,
};

// ─── FSRS item ID ─────────────────────────────────────────────────────────────

/**
 * Curriculum item ID for a given interval config. Every config resolves to a
 * real curriculum item (no orphaned `exercise-interval::` cards) so all practice
 * rolls up into node/track mastery.
 *
 * Direction is the one config dimension promoted to item identity: ascending and
 * descending are separate cards (descending is a distinct ear skill). Harmonic
 * folds into ascending, and key context (in-key vs abstract) is not split —
 * both credit the same functional item.
 */
export function intervalDrillItemId(
  symbol: IntervalSymbol,
  direction: Exclude<IntervalDirection, "mixed">,
  _keyContext: IntervalKeyContext,
): string {
  const base = symbol === "TT" ? "chromatic-intervals::A4" : `diatonic-intervals-major::${symbol}`;
  return direction === "descending" ? `${base}::desc` : base;
}

// ─── Stats key ────────────────────────────────────────────────────────────────

/**
 * Stable key for DrillStatsRecord.
 * Format: `interval-drill|${symbol}|${direction}|${tonic}`
 * where tonic is MIDI number, "rng", or "abstract".
 */
export function intervalDrillStatsKey(
  config: Pick<IntervalExerciseConfig, "keyContext" | "tonic" | "fixedTonicMidi">,
  symbol: IntervalSymbol,
  direction: Exclude<IntervalDirection, "mixed">,
): string {
  const tonicPart =
    config.keyContext === "abstract" ? "abstract" :
    config.tonic === "random" ? "rng" :
    String(config.fixedTonicMidi);
  return `interval-drill|${symbol}|${direction}|${tonicPart}`;
}

// ─── Fixed-key helper ─────────────────────────────────────────────────────────

const SEMI_TO_LETTER: Record<number, { letter: string; accidental: number }> = {
  0:  { letter: "C", accidental: 0 },
  1:  { letter: "D", accidental: -1 },
  2:  { letter: "D", accidental: 0 },
  3:  { letter: "E", accidental: -1 },
  4:  { letter: "E", accidental: 0 },
  5:  { letter: "F", accidental: 0 },
  6:  { letter: "F", accidental: 1 },
  7:  { letter: "G", accidental: 0 },
  8:  { letter: "A", accidental: -1 },
  9:  { letter: "A", accidental: 0 },
  10: { letter: "B", accidental: -1 },
  11: { letter: "B", accidental: 0 },
};

function buildFixedKey(tonicMidi: number): KeyChoice {
  const semi = ((tonicMidi - 60) % 12 + 12) % 12;
  const { letter, accidental } = SEMI_TO_LETTER[semi]!;
  const accSym = accidental === -1 ? "♭" : accidental === 1 ? "♯" : "";
  return {
    letter,
    accidental,
    mode: "ionian",
    tonicMidi,
    name: `${letter}${accSym} ${MODE_DISPLAY_NAMES["ionian"]}`,
  };
}

// ─── Audio helpers ────────────────────────────────────────────────────────────

const CONCRETE_DIRECTIONS: ReadonlyArray<Exclude<IntervalDirection, "mixed">> = [
  "ascending", "descending", "harmonic",
];

const IONIAN_PATTERN = MODE_PATTERNS["ionian"]; // [0, 2, 4, 5, 7, 9, 11]

function appendIntervalNotes(
  builder: AudioPlanBuilder,
  lowerMidi: number,
  upperMidi: number,
  direction: Exclude<IntervalDirection, "mixed">,
): void {
  if (direction === "ascending") {
    builder.addNote(lowerMidi, 0.9, 0.20, "target");
    builder.advance(0.95);
    builder.addNote(upperMidi, 1.3, 0.22, "target");
    builder.advance(1.4);
  } else if (direction === "descending") {
    builder.addNote(upperMidi, 0.9, 0.20, "target");
    builder.advance(0.95);
    builder.addNote(lowerMidi, 1.3, 0.22, "target");
    builder.advance(1.4);
  } else {
    // harmonic
    builder.addChord([lowerMidi, upperMidi], 1.4, 0.18, "harmony");
    builder.advance(1.5);
  }
}

/**
 * Block (harmonic) rendering of an interval — both notes sounded at once.
 * Lets the user hear the two pitches stacked regardless of the drilled melodic
 * direction, and without the cadence. Drives the "Together" button beside Replay.
 */
export function buildIntervalBlockPlan(lowerMidi: number, upperMidi: number): AudioPlan {
  const builder = new AudioPlanBuilder();
  builder.addChord([lowerMidi, upperMidi], 1.6, 0.18, "harmony");
  builder.advance(1.7);
  return builder.build();
}

// ─── Main builder ─────────────────────────────────────────────────────────────

export function buildIntervalExerciseRound(config: IntervalExerciseConfig, seed: number, cadenceOpts?: CadenceOptions): IntervalExerciseRound {
  const rng = new Rng(seed);

  const effectiveDirection: Exclude<IntervalDirection, "mixed"> =
    config.direction === "mixed"
      ? rng.pick(CONCRETE_DIRECTIONS as Array<Exclude<IntervalDirection, "mixed">>)
      : config.direction;

  const builder = new AudioPlanBuilder();

  if (config.keyContext === "abstract") {
    const symbol = rng.pick(config.intervals as IntervalSymbol[]);
    const { semitones } = INTERVAL_POOL[symbol];
    const lowerMidi = 60 + rng.int(5); // C4–E4
    const upperMidi = lowerMidi + semitones;

    builder.addNote(lowerMidi, 0.8, 0.20, "melody");
    builder.advance(0.9);
    appendIntervalNotes(builder, lowerMidi, upperMidi, effectiveDirection);

    return {
      symbol,
      semitones,
      effectiveDirection,
      lowerMidi,
      upperMidi,
      tonicMidi: 0,
      keyName: "",
      degreeIndex: null,
      upperDegreeIndex: null,
      audioPlan: builder.build(),
      teachAudio: buildIntervalTeachPlan(lowerMidi, upperMidi),
    };
  }

  // In-key mode.
  const key: KeyChoice =
    config.tonic === "fixed"
      ? buildFixedKey(config.fixedTonicMidi)
      : pickKey(rng, "major");

  let symbol: IntervalSymbol;
  let degreeIndex: number;

  if (config.drillMode === "solfege-first") {
    // Solfège-first: constrain to (interval, lower degree) pairs where the
    // upper note is also a diatonic scale degree so both notes are nameable.
    const validIntervals = (config.intervals as IntervalSymbol[]).filter(
      (iv) => DIATONIC_LOWER_DEGREES[iv].length > 0,
    );
    const pool = validIntervals.length > 0 ? validIntervals : (config.intervals as IntervalSymbol[]);
    symbol = rng.pick(pool);
    const validDegrees = DIATONIC_LOWER_DEGREES[symbol];
    degreeIndex = rng.pick(validDegrees.length > 0 ? validDegrees : [0, 1, 2, 3, 4, 5, 6]);
  } else if (config.bottomNote === "tonic") {
    symbol = rng.pick(config.intervals as IntervalSymbol[]);
    degreeIndex = 0;
  } else {
    symbol = rng.pick(config.intervals as IntervalSymbol[]);
    degreeIndex = rng.int(7);
  }

  const { semitones } = INTERVAL_POOL[symbol];
  const maxLower = 95 - semitones;
  const lowerMidi = Math.min(key.tonicMidi + 12 + IONIAN_PATTERN[degreeIndex]!, maxLower);
  const upperMidi = lowerMidi + semitones;

  // Determine upper note's diatonic degree index (null if chromatic).
  const upperOffsetFromTonic = ((upperMidi - key.tonicMidi) % 12 + 12) % 12;
  const upperDegreeIdxInPattern = IONIAN_PATTERN.indexOf(upperOffsetFromTonic);
  const upperDegreeIndex = upperDegreeIdxInPattern !== -1 ? upperDegreeIdxInPattern : null;

  appendCadence(builder, key, cadenceOpts);
  appendIntervalNotes(builder, lowerMidi, upperMidi, effectiveDirection);

  return {
    symbol,
    semitones,
    effectiveDirection,
    lowerMidi,
    upperMidi,
    tonicMidi: key.tonicMidi,
    keyName: key.name,
    degreeIndex,
    upperDegreeIndex,
    audioPlan: builder.build(),
    teachAudio: buildIntervalTeachPlan(lowerMidi, upperMidi),
  };
}
